/**
 * The shared state behind useSurfaces, with no network code in it.
 *
 * Kept apart from the loader so setupTests.js can prime it without importing
 * the API client: a module imported by the setup file is loaded before a test
 * file's own jest.mock() of it, and would quietly defeat that mock.
 */
import { deriveSurfaceLists } from './derive';

const EMPTY_LISTS = Object.freeze(deriveSurfaceLists([]));

export const IDLE = Object.freeze({
  status: 'idle', surfaces: [], fingerprint: null, lists: EMPTY_LISTS, error: null,
});

let state = IDLE;
const listeners = new Set();

export const getState = () => state;

export const setState = (next) => {
  state = Object.freeze(next);
  listeners.forEach((l) => l());
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The 'ready' state for a /surfaces response body. */
export const readyState = (body) => {
  const surfaces = Array.isArray(body?.surfaces) ? body.surfaces : [];
  return {
    status: 'ready',
    surfaces,
    fingerprint: body?.fingerprint || null,
    lists: Object.freeze(deriveSurfaceLists(surfaces)),
    error: null,
  };
};

/**
 * Fill the store from a /surfaces response without fetching. For tests, which
 * prime it once from the recorded fixture (setupTests.js) so pages render with
 * the real definitions; anything testing the loading path calls
 * resetSurfaces() first.
 */
export const primeSurfaces = (body) => setState(readyState(body));

/** Forget what was loaded. */
export const resetState = () => setState(IDLE);
